import { Locale } from 'enums/locale';
import { VehicleType } from 'enums/vehicleTypes';
import { VehicleSummary } from './vehicle.type';
import { DropdownOption, DropdownOptionWithDescription } from './types';
import { ReactNode } from 'react';
import { BusinessModel } from 'enums/businessModel';
import { SearchLocationType } from 'enums/SearchLocationType';
import { ExperienceSummary } from './experience/experience-summary.type';

export interface BreadcrumbsLocationData extends LocationData {
  locale: Locale;
  matchType: 'harbor' | 'region' | 'city' | 'country';
  vehicleCount: number;
  experienceCount: number;
}

export interface BreadcrumbsData {
  location: BreadcrumbsLocationData | null;
  typeLabel: VehicleType | null;
}

export interface PaginatedVehicleWithBreadcrumbs extends BreadcrumbsData {
  vehicles: PaginatedResponse<VehicleSummary[]>;
}

export interface PaginatedExperienceWithBreadcrumbs extends BreadcrumbsData {
  experiences: PaginatedResponse<ExperienceSummary[]>;
}

export function isSearchResponseExperience(
  apiVehicles:
    | PaginatedVehicleWithBreadcrumbs
    | PaginatedExperienceWithBreadcrumbs
): apiVehicles is PaginatedExperienceWithBreadcrumbs {
  return 'experiences' in apiVehicles;
}

export type PaginatedVehicle = PaginatedResponse<VehicleSummary[]>;

export interface Pagination {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
  number: number;
  sort: Sort;
  pageable: Pageable;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface SearchState {
  name: string;
  slug: string;
  type: SearchLocationType;
  children?: SearchState[];
  vehicleCount: number;
  experienceCount?: number;
  parentSlug?: string;
  hidden?: boolean;
}

export interface PaginationQueries extends Pick<Pagination, 'page' | 'size'> {
  sort?: string[];
}

export interface PaginatedResponse<T> extends Pagination {
  content: T;
}

export interface Price {
  currency: string;
  amount: number;
}

export interface Pageable {
  offset: number;
  sort: Sort;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  unpaged: boolean;
}

export interface Sort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}

export interface FilterOptions {
  priceRange: FilterRange;
  vehicleTypes: string[];
  spokenLanguagesByOwner: string[];
  usageConditions: DropdownOptionWithDescription[];
  vehicleFacilities: DropdownOptionWithDescription[];
  // capacityRange: FilterRange;
  // diningCapacityRange: FilterRange;
  minimumRentDurationRange: FilterRange;
  minimumRentDurationOnSpecialDaysRange: FilterRange;
  ratingRange: FilterRange;
  ratingSelections: number[];
  locations: LocationData[];
}

export interface FilterRange {
  min: number;
  max: number;
}

export interface LocationData {
  harborName: string;
  regionName: string;
  cityName: string;
  countryName: string;
  harborSlug: string;
  regionSlug: string;
  citySlug: string;
  countrySlug: string;
}

export interface FilterState {
  onlyImmediatelyReservable: boolean;
  onlySuitableForWinter: boolean;
  canReserveForOneHour: boolean;
  rating: number;
  // capacity: FilterRange;
  // isDiningCapacity: boolean;
  vehicleTypes: VehicleType[];
  usageConditions: number[];
  facilities: number[];
  priceRange: FilterRange;
}

export interface FilterParameters {
  priceRange?: Partial<FilterRange>;
  locationSlugs?: string[];
  vehicleTypes?: VehicleType[];
  minimumRentDuration?: Partial<FilterRange>;
  onlyImmediatelyReservable?: boolean;
  onlySuitableForWinter?: boolean;
  spokenLanguagesByOwner?: string[];
  withAccommodation?: boolean;
  // capacity?: Partial<FilterRange>;
  // diningCapacity?: Partial<FilterRange>;
  usageConditions?: number[];
  vehicleFacilities?: number[];
  rating?: Partial<FilterRange>;
}

export interface SearchParameters {
  startTime?: Date | string;
  endTime?: Date | string;
  businessModel?: BusinessModel | null;
  flexDates?: boolean;
  numberOfPeople?: number;
  location?: SearchState;
}

export interface ExperienceSearchParameters {
  date?: Date | string;
  locationSlug?: string;
  ticketCount?: number;
}

export interface SearchHourOption<T> extends DropdownOption<T> {
  icon: ReactNode;
  prefix?: string | null;
  dayOffset: number;
}

export interface SearchMappedQueries {
  startTime?: Date;
  endTime?: Date;
  numberOfPeople?: number;
  businessModel?: BusinessModel;
  flexDates?: boolean;
}

export interface SearchDemandDTO {
  startTime?: Date;
  endTime?: Date;
  businessModel?: BusinessModel;
  flexDates?: boolean;
  numberOfPeople?: number;
  fromLocation?: string;
}
